import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { Family, FormErrors, prettyFamilyStatus } from "./types";
import { FamilyStatus } from "protogen/advisors_service_pb";
import { useState, useEffect, useContext } from "react";
import AdvisorSelect from "../creation/AdvisorSelect";
import AddressAutocomplete, { Location } from "../common/AddressAutocomplete";
import FamilyPlanDetails from "./FamilyPlanDetails";
import { hasRole, ROLE_ADMIN } from "../../common/userUtils";
import { CurrentUserContext } from "../context/RequireAuth";
import { DatePicker } from "@mui/x-date-pickers";
import { ChevronDown } from "lucide-react";
import useIsMobile from "components/hooks/useIsMobile";

export const EndDate = ({
  date,
  onDateChange,
  error,
  disabled = false,
}: {
  date: Date | null;
  onDateChange: (date: Date | null) => void;
  error?: string;
  disabled?: boolean;
}) => {
  const [dateOpen, setDateOpen] = useState(false);
  return (
    <DatePicker
      sx={{
        width: "calc(33% - 8px)",
        ".MuiButtonBase-root": { marginRight: "unset" },
      }}
      label="End date"
      defaultValue={date}
      disabled={disabled}
      onChange={(dte) => {
        if (dte === null) return;
        onDateChange(dte);
      }}
      format="MM/dd/yyyy"
      open={dateOpen}
      onClose={() => setDateOpen(false)}
      slotProps={{
        textField: {
          error: !!error,
          helperText: error,
          onClick: () => setDateOpen(true),
        },
      }}
    />
  );
};

interface Props {
  familyData: Family;
  setFamilyData: (f: Partial<Family>) => void;
  errors: FormErrors | null;
  selectAdvisor?: boolean;
  stacked?: boolean;
  enableStatusSelect?: boolean;
}

export const PlanDetailsForm = ({
  familyData,
  setFamilyData,
  errors,
  stacked = true,
}: Props) => {
  const currentUser = useContext(CurrentUserContext);
  const isAdmin = hasRole(currentUser, ROLE_ADMIN);
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "12px",
      }}
    >
      {!familyData.isTestFamily && (
        <FamilyPlanDetails
          disabled={!isAdmin && familyData.status === FamilyStatus.ACTIVE}
          planDetails={{
            startDate: familyData.startDate
              ? new Date(familyData.startDate)
              : undefined,
            product: familyData.product,
            coupon: familyData.introCoupon,
          }}
          initialProductName={familyData.productName}
          initialCouponId={familyData.introCouponId}
          errors={{
            startDate: errors?.startDate || "",
          }}
          onPlanDetailsChange={(details) => {
            setFamilyData({
              ...("product" in details ? { product: details.product } : {}),
              ...("coupon" in details ? { introCoupon: details.coupon } : {}),
              ...("startDate" in details
                ? {
                    startDate: details.startDate
                      ? details.startDate.toLocaleDateString()
                      : null,
                  }
                : {}),
            });
          }}
          stacked={stacked}
        />
      )}
    </Box>
  );
};

export default ({
  familyData,
  setFamilyData,
  errors,
  selectAdvisor = false,
  enableStatusSelect = true,
}: Props) => {
  const currentUser = useContext(CurrentUserContext);
  const isAdmin = hasRole(currentUser, ROLE_ADMIN);
  const [isLoaded, setIsLoaded] = useState(false);
  const [parsedAddress, setParsedAddress] = useState<Location>();
  useEffect(() => {
    if (!familyData.address) return;
    setParsedAddress(JSON.parse(familyData.address));
    setIsLoaded(true);
  }, [familyData.address]);
  const isMobile = useIsMobile();

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "12px",
        marginTop: "16px",
      }}
    >
      <Typography variant={"bodyHeavy"}>Family details</Typography>
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          gap: "16px",
        }}
      >
        <TextField
          fullWidth
          error={!!errors?.name}
          helperText={errors?.name}
          value={familyData.name}
          onChange={(e) =>
            setFamilyData({ ...familyData, name: e.target.value })
          }
          label="Family Name"
          margin="none"
          size="small"
          sx={{
            flex: 1,
          }}
        />
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: "16px",
            flex: 1,
          }}
        >
          <FormControl fullWidth>
            <InputLabel id="select-label">Status</InputLabel>
            <Select
              fullWidth
              labelId="select-label"
              id="select-label"
              value={familyData.status}
              label="Status"
              IconComponent={ChevronDown}
              disabled={
                !(isAdmin || enableStatusSelect) || familyData.isTestFamily
              }
              onChange={(e) =>
                setFamilyData({
                  ...familyData,
                  status: e.target.value as FamilyStatus,
                })
              }
            >
              <MenuItem value={FamilyStatus.ACTIVE}>
                {prettyFamilyStatus(FamilyStatus.ACTIVE)}
              </MenuItem>
              <MenuItem value={FamilyStatus.PROSPECT}>
                {prettyFamilyStatus(FamilyStatus.PROSPECT)}
              </MenuItem>
              <MenuItem value={FamilyStatus.DEMO}>
                {prettyFamilyStatus(FamilyStatus.DEMO)}
              </MenuItem>
              <MenuItem value={FamilyStatus.TEST}>
                {prettyFamilyStatus(FamilyStatus.TEST)}
              </MenuItem>
              <MenuItem value={FamilyStatus.DEACTIVATED}>
                {prettyFamilyStatus(FamilyStatus.DEACTIVATED)}
              </MenuItem>
              <MenuItem value={FamilyStatus.DIGITAL_DEACTIVATED}>
                {prettyFamilyStatus(FamilyStatus.DIGITAL_DEACTIVATED)}
              </MenuItem>
              <MenuItem value={FamilyStatus.DIGITAL_DEACTIVATED_TRIAL}>
                {prettyFamilyStatus(FamilyStatus.DIGITAL_DEACTIVATED_TRIAL)}
              </MenuItem>
              <MenuItem value={FamilyStatus.DEACTIVATED_PROSPECT}>
                {prettyFamilyStatus(FamilyStatus.DEACTIVATED_PROSPECT)}
              </MenuItem>
              <MenuItem value={FamilyStatus.PREACTIVATION}>
                {prettyFamilyStatus(FamilyStatus.PREACTIVATION)}
              </MenuItem>
              <MenuItem value={FamilyStatus.UNPAID_SIGNUP}>
                {prettyFamilyStatus(FamilyStatus.UNPAID_SIGNUP)}
              </MenuItem>
              <MenuItem value={FamilyStatus.DIGITAL_SUBSCRIPTION}>
                {prettyFamilyStatus(FamilyStatus.DIGITAL_SUBSCRIPTION)}
              </MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>
      {isLoaded && (
        <AddressAutocomplete
          label={"Address"}
          error={errors?.location}
          initialValue={parsedAddress?.formattedAddress || ""}
          setValue={(location) => {
            setParsedAddress(location);
            setFamilyData({
              ...familyData,
              address: JSON.stringify(location),
            });
          }}
          handleCopy={true}
        />
      )}
      {selectAdvisor && (
        <AdvisorSelect
          title="Advisor"
          disabled={false}
          isAdmin={true} // Can't get into this state without being an admin
          error={errors?.advisorRef}
          selectedAdvisor={familyData.advisorRef || null}
          onChange={(v) => setFamilyData({ ...familyData, advisorRef: v! })}
        />
      )}
    </Box>
  );
};
